import React, { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BatteryCharging,
  Camera,
  ClipboardCheck,
  Crosshair,
  Eye,
  Factory,
  Gauge,
  Image as ImageIcon,
  Leaf,
  Linkedin,
  LocateFixed,
  LockKeyhole,
  Mail,
  Map,
  Menu,
  Moon,
  Package,
  Plane,
  Radio,
  Route,
  ScanLine,
  ShieldCheck,
  Siren,
  TriangleAlert,
  Video,
  X,
} from "lucide-react";
import "./HolomPage.css";
import HolomExplorer from "./HolomExplorer";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";

const HOLOM_HERO_IMAGE = "https://cdn.sanity.io/images/ik49mluy/production/e552948206ed1478a0f4a8ebd76e37c331dabf58-6020x4024.jpg";
const HOLOM_HERO_SRC_SET = [900, 1400, 2000]
  .map((width) => `${HOLOM_HERO_IMAGE}?w=${width}&auto=format&q=85&fit=max ${width}w`)
  .join(", ");

const CONTACT_ENDPOINT = (() => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/$/, "");
  if (!baseUrl || (typeof window !== "undefined" && !/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) && /localhost|127\.0\.0\.1/.test(baseUrl))) {
    return "/api/contact";
  }
  return `${baseUrl}/api/contact`;
})();

const METRICS = [
  ["90 min", null, "Flight Endurance"],
  ["74.6 mi", "120 km", "Mission Range"],
  ["5.5 lb", "2.5 kg", "Payload Capacity"],
  ["47.8 mph", "77 km/h", "Cruise Speed"],
  ["44.1 lb", "20 kg", "MTOW"],
];

const WHY_CARDS = [
  { icon: ArrowUp, title: "Vertical Takeoff & Landing", copy: "Deploy without a runway, catapult or conventional recovery system." },
  { icon: Plane, title: "Efficient Fixed-Wing Cruise", copy: "Transition automatically into forward flight for efficient coverage of extended mission areas." },
  { icon: Route, title: "Autonomous Operations", copy: "Plan missions, execute autonomous transitions and enable automated return procedures." },
  { icon: Package, title: "Mission-Configurable Architecture", copy: "Integrate payloads, communications and mission systems according to operational requirements." },
];

const CONFIGURATIONS = {
  general: {
    label: "GENERAL PURPOSE",
    id: "general-purpose",
    headline: "One Aircraft. Multiple Missions.",
    copy: "Holom is designed for organizations that need to collect high-value aerial data across large or difficult-to-access areas while minimizing deployment infrastructure. Its electric VTOL architecture enables operations from confined or improvised locations, followed by efficient fixed-wing flight for extended mission coverage.",
    imageLabel: "General Purpose operational image",
    imageNote: "Holom operating along linear infrastructure",
    imageSrc: "/images/Oil_duct.jpg",
    imageAlt: "Holom VTOL UAS inspecting an oil pipeline corridor",
    applications: [
      { icon: Eye, title: "Surveillance & Monitoring", copy: "Maintain persistent aerial observation across large sites, corridors and remote operating areas." },
      { icon: ScanLine, title: "ISR & Situational Awareness", copy: "Collect mission-relevant imagery and data to support faster, better-informed operational decisions." },
      { icon: Factory, title: "Infrastructure Inspection", copy: "Inspect power lines, pipelines, roads, industrial facilities and distributed infrastructure across large areas." },
      { icon: Map, title: "Mapping & Surveying", copy: "Capture high-resolution imagery for photogrammetry, terrain analysis, construction monitoring and geospatial workflows." },
      { icon: TriangleAlert, title: "Emergency Response", copy: "Rapidly deploy aerial intelligence following natural disasters, infrastructure failures or other emergency events." },
      { icon: Leaf, title: "Environmental Monitoring", copy: "Monitor large areas using RGB, thermal, multispectral or other mission-specific sensors." },
    ],
    payloads: [
      { icon: Camera, title: "High-Resolution EO", copy: "Inspection, mapping, visual intelligence and asset documentation." },
      { icon: Map, title: "Mapping & Photogrammetry", copy: "Orthomosaics, surveying, terrain modeling and 3D reconstruction." },
      { icon: ScanLine, title: "LiDAR", copy: "Terrain, vegetation, infrastructure and geospatial analysis." },
      { icon: Leaf, title: "Multispectral", copy: "Environmental monitoring, agriculture and specialized analysis." },
      { icon: Eye, title: "EO/IR", copy: "Thermal inspection, search and rescue and day/night operations." },
      { icon: Package, title: "Specialized Payloads", copy: "Compatible third-party sensors and mission equipment." },
    ],
  },
  tactical: {
    label: "TACTICAL",
    id: "tactical",
    headline: "Persistent Intelligence. Rapid Deployment.",
    copy: "The Tactical Holom configuration is designed for intelligence, surveillance, reconnaissance and situational-awareness missions requiring rapid deployment, extended aerial coverage and real-time information.",
    imageLabel: "Tactical Holom aircraft image",
    imageNote: "Neutral dark background — no military context",
    imageSrc: "/images/Composite_Tactical.png",
    imageAlt: "Tactical Holom VTOL UAS operating over mountainous terrain",
    applications: [
      { icon: Moon, title: "Day / Night Surveillance", copy: "Integrate EO/IR payloads for day and night observation across extended areas." },
      { icon: Eye, title: "ISR", copy: "Support intelligence, surveillance and reconnaissance missions with persistent airborne observation." },
      { icon: Crosshair, title: "Target Tracking", copy: "Support compatible payloads capable of stabilized observation and tracking of vehicles or people." },
      { icon: LocateFixed, title: "Geolocation", copy: "Enable compatible mission systems to provide geolocation of points of interest." },
      { icon: Siren, title: "Public Safety", copy: "Support law enforcement, emergency response and public-safety operations with real-time aerial intelligence." },
      { icon: ShieldCheck, title: "Perimeter & Infrastructure Security", copy: "Monitor large sites, borders, infrastructure corridors and geographically distributed assets." },
    ],
    payloads: [
      { icon: Eye, title: "Lightweight EO/IR", copy: "Stabilized observation with minimal impact on aircraft endurance." },
      { icon: LocateFixed, title: "Long-Range EO/IR", copy: "Long-range day/night observation with stabilized pan and tilt." },
      { icon: Camera, title: "High-Definition ISR", copy: "High-definition EO/IR for tracking, geolocation and advanced mission metadata." },
      { icon: ScanLine, title: "Edge AI & Advanced Sensing", copy: "EO/IR/LRF configurations with onboard processing for detection, tracking and sensor fusion." },
    ],
  },
};

const SPEC_GROUPS = [
  {
    title: "Aircraft",
    icon: Plane,
    specs: [["Aircraft Type", "Electric Fixed-Wing VTOL UAS"], ["Architecture", "Lift + Cruise"], ["Wingspan", "11.8 ft (3.6 m)"], ["Length", "6.9 ft (2.1 m)"], ["MTOW", "44.1 lb (20 kg)"], ["Payload", "Up to 5.5 lb (2.5 kg)"], ["Airframe", "Carbon fiber composite"]],
  },
  {
    title: "Performance",
    icon: Gauge,
    specs: [["Endurance", "Up to 90 minutes"], ["Mission Range", "Up to 74.6 mi (120 km)"], ["Cruise Speed", "42 kt / 47.8 mph (77 km/h)"], ["Maximum Speed", "81 kt / 93.2 mph (150 km/h)"]],
  },
  {
    title: "Operations",
    icon: Route,
    specs: [["Takeoff Altitude", "Up to 14,760 ft MSL (4,500 m MSL)"], ["Operating Altitude", "Up to 18,000 ft MSL (5,500 m MSL)"], ["Takeoff / Landing Wind", "Up to 11.6 kt (6 m/s)"], ["Cruise Wind", "Up to 23.3 kt (12 m/s)"], ["VTOL Area", "Approx. 31.5 ft diameter (9.6 m)"], ["Flight Mode", "Autonomous VTOL Lift + Cruise"]],
  },
  {
    title: "Communications",
    icon: Radio,
    specs: [["Data Link Range", "12.4 to 74.6 mi (20 to 120 km), configuration dependent"], ["Frequencies", "933 MHz / 2.4 GHz, configuration dependent"], ["Encryption", "AES 128 / 256, configuration dependent"]],
  },
];

const CONTROL_CAPABILITIES = [
  { icon: Map, title: "Mission Planning", copy: "Plan routes, waypoints and mission parameters before deployment." },
  { icon: Gauge, title: "Real-Time Telemetry", copy: "Monitor aircraft status and mission information throughout the operation." },
  { icon: Video, title: "Live Video", copy: "Receive real-time imagery from compatible payload configurations." },
  { icon: LockKeyhole, title: "Secure Communications", copy: "Encrypted communication options are available for mission-critical applications." },
  { icon: Radio, title: "Extended-Range Links", copy: "Configure communications according to operational range and mission requirements." },
];

const AUTONOMY_STEPS = [
  "Automatic VTOL Takeoff",
  "Automatic Vertical-to-Forward Transition",
  "Advanced Mission Planning",
  "Autonomous Flight",
  "Automatic Return-to-Safe-Point Logic",
  "Lost-Link Emergency Procedures",
  "Real-Time Telemetry",
];

const INDUSTRIES = ["Defense", "Public Safety", "Government", "Energy", "Utilities", "Infrastructure", "Mining", "Mapping & Surveying", "Environmental", "Emergency Response", "Systems Integration", "Other"];
const INTERESTS = ["General Purpose Holom", "Tactical Holom", "Payload Integration", "Demonstration", "Distribution / Partnership", "Other"];

function trackEvent(name, properties = {}) {
  if (typeof window === "undefined") return;
  window.gtag?.("event", name, properties);
  window.posthog?.capture?.(name, properties);
}

function MediaPlaceholder({
  label,
  note,
  src,
  srcSet,
  sizes,
  alt,
  tone = "light",
  compact = false,
  fit = "cover",
  position,
  priority = false,
  className = "",
}) {
  const { t } = useI18n();
  if (src) {
    return (
      <figure className={`holom-media holom-media--${tone} holom-media--${fit} ${compact ? "holom-media--compact" : ""} ${className}`}>
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={t(alt || label)}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          style={position ? { objectPosition: position } : undefined}
        />
      </figure>
    );
  }

  return (
    <div
      className={`holom-placeholder holom-placeholder--${tone} ${compact ? "holom-placeholder--compact" : ""} ${className}`}
      role="img"
      aria-label={`${t(label)} ${t("placeholder")}`}
    >
      <ImageIcon aria-hidden="true" />
      <span>{t("IMAGE PLACEHOLDER")}</span>
      <strong>{t(label)}</strong>
      {note ? <small>{t(note)}</small> : null}
    </div>
  );
}

function SectionHeading({ eyebrow, title, copy, align = "left", light = false }) {
  const { t } = useI18n();
  return (
    <div className={`holom-section-heading holom-section-heading--${align} ${light ? "holom-section-heading--light" : ""}`}>
      <p className="holom-eyebrow">{t(eyebrow)}</p>
      <h2>{t(title)}</h2>
      {copy ? <p>{t(copy)}</p> : null}
    </div>
  );
}

function IconCard({ icon: Icon, title, copy, dark = false }) {
  const { t } = useI18n();
  return (
    <article className={`holom-icon-card ${dark ? "holom-icon-card--dark" : ""}`}>
      <div className="holom-icon-card__icon"><Icon aria-hidden="true" /></div>
      <h3>{t(title)}</h3>
      <p>{t(copy)}</p>
    </article>
  );
}

function HolomHeader({ menuOpen, setMenuOpen }) {
  const { t } = useI18n();
  const nav = [
    ["Overview", "#overview"],
    ["Configurations", "#configurations"],
    ["Applications", "#applications"],
    ["Specifications", "#specifications"],
    ["Payloads", "#payloads"],
  ];

  return (
    <header className="holom-header">
      <a className="holom-logo-link" href="/" aria-label={t("Imago Aerospace home")}><img className="holom-logo" src="/CandidatoFinal%20Mini.png" alt="Imago Aerospace" /></a>
      <nav className={`holom-nav ${menuOpen ? "holom-nav--open" : ""}`} aria-label={t("Holom page navigation")}>
        {nav.map(([label, href]) => <a key={href} href={href} onClick={() => setMenuOpen(false)}>{t(label)}</a>)}
      </nav>
      <div className="holom-header__actions">
        <LanguageSwitcher className="holom-language-switcher" />
        <a className="holom-button holom-button--small holom-header__cta" href="#contact" onClick={() => trackEvent("holom_contact_click")}>{t("Contact Us")} <ArrowRight aria-hidden="true" /></a>
        <button className="holom-menu-button" type="button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-label={t(menuOpen ? "Close navigation" : "Open navigation")}>
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>
    </header>
  );
}

function ContactForm({ configuration }) {
  const { language, t } = useI18n();
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [timestamp, setTimestamp] = useState(() => Date.now());

  async function handleSubmit(event) {
    event.preventDefault();
    if (status === "sending") return;
    const form = event.currentTarget;
    const data = new FormData(form);
    if (data.get("website")) return;
    setStatus("sending");
    setMessage("");

    const params = new URLSearchParams(window.location.search);
    const interest = data.get("interest")?.toString() || "";
    const details = [
      "Page: Holom Landing Page",
      `Configuration selected: ${configuration === "tactical" ? "Tactical" : "General Purpose"}`,
      `Country: ${data.get("country") || ""}`,
      `Industry: ${data.get("industry") || ""}`,
      `Interest: ${interest}`,
      `Visitor language: ${language === "es" ? "Spanish" : "English"}`,
      `UTM source: ${params.get("utm_source") || "—"}`,
      `UTM medium: ${params.get("utm_medium") || "—"}`,
      `UTM campaign: ${params.get("utm_campaign") || "—"}`,
      `UTM content: ${params.get("utm_content") || "—"}`,
      "",
      "Mission:",
      data.get("mission")?.toString().trim() || "No mission details supplied.",
    ].join("\n");

    const payload = {
      name: `${data.get("firstName") || ""} ${data.get("lastName") || ""}`.trim(),
      company: data.get("company")?.toString().trim() || undefined,
      email: data.get("email")?.toString().trim() || "",
      message: details,
      formTimestamp: timestamp,
      honeypot: undefined,
    };

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Request failed");
      form.reset();
      setTimestamp(Date.now());
      setStatus("success");
      setMessage(t("Thank you. Our team will contact you shortly."));
      trackEvent("holom_form_submitted", { configuration, industry: data.get("industry") || "", country: data.get("country") || "" });
      if (interest === "Demonstration") trackEvent("holom_demo_request", { configuration });
    } catch (error) {
      setStatus("error");
      setMessage(t("We couldn't submit your request. Please try again or email hello@imago.aero."));
    }
  }

  return (
    <form className="holom-contact-form" onSubmit={handleSubmit} onFocus={() => trackEvent("holom_form_started", { configuration })}>
      <input type="hidden" name="formTimestamp" value={timestamp} />
      <div className="holom-honeypot" aria-hidden="true"><label htmlFor="holom-website">{t("Website")}</label><input id="holom-website" name="website" tabIndex={-1} autoComplete="off" /></div>
      <div className="holom-field"><label htmlFor="holom-first-name">{t("First Name")}</label><input id="holom-first-name" name="firstName" autoComplete="given-name" required /></div>
      <div className="holom-field"><label htmlFor="holom-last-name">{t("Last Name")}</label><input id="holom-last-name" name="lastName" autoComplete="family-name" required /></div>
      <div className="holom-field"><label htmlFor="holom-email">{t("Work Email")}</label><input id="holom-email" name="email" type="email" autoComplete="email" required /></div>
      <div className="holom-field"><label htmlFor="holom-company">{t("Company / Organization")}</label><input id="holom-company" name="company" autoComplete="organization" required /></div>
      <div className="holom-field"><label htmlFor="holom-country">{t("Country")}</label><input id="holom-country" name="country" autoComplete="country-name" required /></div>
      <div className="holom-field"><label htmlFor="holom-industry">{t("Industry")}</label><select id="holom-industry" name="industry" defaultValue="" required><option value="" disabled>{t("Select industry")}</option>{INDUSTRIES.map((item) => <option key={item} value={item}>{t(item)}</option>)}</select></div>
      <div className="holom-field holom-field--wide"><label htmlFor="holom-interest">{t("I'm interested in")}</label><select id="holom-interest" name="interest" defaultValue="" required><option value="" disabled>{t("Select an option")}</option>{INTERESTS.map((item) => <option key={item} value={item}>{t(item)}</option>)}</select></div>
      <div className="holom-field holom-field--wide"><label htmlFor="holom-mission">{t("Tell us about your mission")}</label><textarea id="holom-mission" name="mission" rows="5" required /></div>
      <div className="holom-form-footer">
        <button className="holom-button" type="submit" disabled={status === "sending"}>{t(status === "sending" ? "Submitting…" : "Submit Request")}<ArrowRight aria-hidden="true" /></button>
        <p className={`holom-form-message holom-form-message--${status}`} aria-live="polite">{message}</p>
      </div>
    </form>
  );
}

export default function HolomPage() {
  const { language, t } = useI18n();
  const getInitialConfiguration = () => {
    if (typeof window === "undefined") return "general";
    const requested = new URLSearchParams(window.location.search).get("configuration");
    return window.location.hash === "#tactical" || requested === "tactical" ? "tactical" : "general";
  };
  const [configuration, setConfiguration] = useState(getInitialConfiguration);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeConfiguration = CONFIGURATIONS[configuration];

  useEffect(() => {
    const originalTitle = document.title;
    document.title = language === "es" ? "Holom UAS VTOL eléctrico de ala fija | Imago Aerospace" : "Holom Electric Fixed-Wing VTOL UAS | Imago Aerospace";
    const metadata = {
      description: language === "es" ? "Descubre Holom, un UAS VTOL eléctrico de ala fija para vigilancia, ISR, inspección, cartografía y operaciones críticas." : "Discover Holom, an electric fixed-wing VTOL UAS for surveillance, ISR, inspection, mapping and mission-critical operations.",
      "og:title": "Holom | Electric Fixed-Wing VTOL UAS",
      "og:description": language === "es" ? "Despegue vertical. Eficiencia de ala fija. Una plataforma UAS configurable para misiones comerciales, industriales y tácticas." : "Vertical takeoff. Fixed-wing efficiency. One configurable UAS platform for commercial, industrial and tactical missions.",
      "og:image": `${HOLOM_HERO_IMAGE}?w=1600&auto=format&q=85&fit=max`,
    };
    const touched = [];
    Object.entries(metadata).forEach(([key, content]) => {
      const attribute = key.startsWith("og:") ? "property" : "name";
      let node = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!node) {
        node = document.createElement("meta");
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      touched.push([node, node.getAttribute("content")]);
      node.setAttribute("content", content);
    });
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Product", name: "Holom", brand: { "@type": "Brand", name: "Imago Aerospace" }, manufacturer: { "@type": "Organization", name: "Imago Aerospace" }, description: metadata.description });
    document.head.appendChild(schema);
    return () => {
      document.title = originalTitle;
      touched.forEach(([node, content]) => content === null ? node.remove() : node.setAttribute("content", content));
      schema.remove();
    };
  }, [language]);

  useEffect(() => {
    trackEvent("holom_page_view", { configuration: getInitialConfiguration() });
  }, []);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const events = {
      specifications: ["holom_specifications_viewed"],
      payloads: ["holom_payloads_viewed"],
    };
    const viewed = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || viewed.has(entry.target.id)) return;
        viewed.add(entry.target.id);
        events[entry.target.id].forEach((eventName) => trackEvent(eventName, { configuration }));
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    Object.keys(events).forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [configuration]);

  function selectConfiguration(next) {
    setConfiguration(next);
    const item = CONFIGURATIONS[next];
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${item.id}`);
    trackEvent(next === "tactical" ? "holom_tactical_selected" : "holom_general_purpose_selected");
  }

  return (
    <main className="holom-page">
      <HolomHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <section className="holom-hero" id="overview">
        <div className="holom-hero__copy">
          <p className="holom-eyebrow">HOLOM</p>
          <p className="holom-kicker">{t("Electric Fixed-Wing VTOL UAS")}</p>
          <h1>{t("Fixed-Wing Efficiency.")}<br /><em>{t("Vertical Takeoff Freedom.")}</em></h1>
          <p className="holom-lede">{t("Holom is an all-electric fixed-wing VTOL UAS designed for surveillance, ISR, inspection, mapping and mission-critical operations. With up to 90 minutes of endurance, up to 120 km of mission range and up to 2.5 kg of payload capacity, Holom combines vertical deployment with efficient fixed-wing flight.")}</p>
          <p className="holom-hero__support">{t("One aircraft platform. Configurable for commercial, industrial and tactical missions.")}</p>
          <div className="holom-actions">
            <a className="holom-button" href="#why-holom">{t("Explore Holom")} <ArrowRight aria-hidden="true" /></a>
            <a className="holom-text-link" href="#contact" onClick={() => trackEvent("holom_contact_click")}>{t("Talk to Our Team")} <ArrowRight aria-hidden="true" /></a>
          </div>
        </div>
        <MediaPlaceholder
          tone="dark"
          label="Hero Holom aircraft"
          src={`${HOLOM_HERO_IMAGE}?w=2000&auto=format&q=85&fit=max`}
          srcSet={HOLOM_HERO_SRC_SET}
          sizes="(max-width: 900px) calc(100vw - 32px), 52vw"
          alt="Holom V-15 fixed-wing VTOL UAS in flight over mountainous terrain"
          position="68% center"
          priority
        />
        <a className="holom-scroll-cue" href="#performance"><span>{t("Scroll to explore")}</span><ArrowDown aria-hidden="true" /></a>
      </section>

      <section className="holom-performance" id="performance" aria-label={t("Quick performance overview")}>
        <div className="holom-performance__grid">
          {METRICS.map(([imperial, metric, label]) => <div className="holom-metric" key={label}><strong>{imperial}{metric ? <small> ({metric})</small> : null}</strong><span>{t(label)}</span></div>)}
        </div>
        <div className="holom-compliance-strip">
          <span><BatteryCharging aria-hidden="true" />{t("Electric VTOL")}</span>
          <span><ShieldCheck aria-hidden="true" />{t("NDAA Compliant")}</span>
          <span><ClipboardCheck aria-hidden="true" />{t("FAA Certification in Progress")}</span>
        </div>
      </section>

      <section className="holom-section holom-why" id="why-holom">
        <div className="holom-container">
          <SectionHeading eyebrow="WHY HOLOM" title="The Reach of a Fixed Wing. The Flexibility of VTOL." copy="Traditional fixed-wing UAS offer efficient flight but typically require launch and recovery infrastructure. Multirotors provide operational flexibility but sacrifice endurance and range. Holom combines both capabilities in a single electric aircraft." />
          <div className="holom-card-grid holom-card-grid--four">{WHY_CARDS.map((card) => <IconCard key={card.title} {...card} />)}</div>
        </div>
      </section>

      <HolomExplorer />

      <section className="holom-section holom-configurations" id="configurations">
        <div className="holom-container">
          <SectionHeading eyebrow="CONFIGURATIONS" title="One Platform. Built Around the Mission." copy="Holom uses a common electric fixed-wing VTOL aircraft architecture that can be configured with different payloads, communications systems and ground-control solutions according to mission requirements." light />
          <div className="holom-tabs" role="tablist" aria-label={t("Holom mission configuration")}>
            {Object.entries(CONFIGURATIONS).map(([key, item]) => (
              <button key={key} role="tab" aria-selected={configuration === key} aria-controls="holom-configuration-panel" className={configuration === key ? "is-active" : ""} onClick={() => selectConfiguration(key)}>{t(item.label)}</button>
            ))}
          </div>
          <div className="holom-configuration-panel" id="holom-configuration-panel" role="tabpanel">
            <div className="holom-configuration-copy" id={activeConfiguration.id}>
              <p className="holom-eyebrow">{t(activeConfiguration.label)}</p>
              <h3>{t(activeConfiguration.headline)}</h3>
              <p>{t(activeConfiguration.copy)}</p>
              <a className="holom-text-link holom-text-link--light" href="#contact">{t("Talk to Our Team")} <ArrowRight aria-hidden="true" /></a>
            </div>
            <MediaPlaceholder
              tone="dark"
              className="holom-configuration-media"
              label={activeConfiguration.imageLabel}
              note={activeConfiguration.imageNote}
              src={activeConfiguration.imageSrc}
              alt={activeConfiguration.imageAlt}
              position="center"
            />
          </div>
          <div className="holom-applications" id="applications">
            <div className="holom-applications__head"><p className="holom-eyebrow">{t("MISSION APPLICATIONS")}</p><h3>{t("Built to answer real operational needs.")}</h3></div>
            <div className="holom-card-grid holom-card-grid--three">{activeConfiguration.applications.map((card) => <IconCard key={card.title} {...card} dark />)}</div>
          </div>
        </div>
      </section>

      <section className="holom-section holom-specifications" id="specifications">
        <div className="holom-container">
          <div className="holom-specifications__intro">
            <SectionHeading eyebrow="TECHNICAL SPECIFICATIONS" title="Performance engineered into one common platform." copy="Core aircraft performance remains common across General Purpose and Tactical configurations." />
            <MediaPlaceholder
              className="holom-specifications-media"
              label="Holom technical side profile"
              src="/images/Holom_Dossier.png?v=20260825"
              alt="Holom V-15 technical aircraft profile"
              fit="contain"
              compact
            />
          </div>
          <div className="holom-spec-grid">
            {SPEC_GROUPS.map(({ title, icon: Icon, specs }) => (
              <article className="holom-spec-group" key={title}>
                <div className="holom-spec-group__title"><Icon aria-hidden="true" /><h3>{t(title)}</h3></div>
                <dl>{specs.map(([term, value]) => <div key={term}><dt>{t(term)}</dt><dd>{t(value)}</dd></div>)}</dl>
              </article>
            ))}
          </div>
          <p className="holom-disclaimer">{t("Actual performance may vary depending on aircraft configuration, payload, environmental conditions, communications equipment and mission profile.")}</p>
        </div>
      </section>

      <section className="holom-section holom-payloads" id="payloads">
        <div className="holom-container">
          <SectionHeading eyebrow="PAYLOADS" title="A Platform Built Around the Mission" copy="Holom can be configured with different payloads and mission systems according to operational requirements." align="center" />
          <div className="holom-payload-layout">
            <div className="holom-card-grid holom-card-grid--two">{activeConfiguration.payloads.map((card) => <IconCard key={card.title} {...card} />)}</div>
            <MediaPlaceholder
              label={configuration === "tactical" ? "EO/IR payload options" : "Existing Imago payload integrations"}
              note={configuration === "tactical" ? "ORUS-L camera payload" : "Use real integration photography when available"}
              src="/images/orus-l_camera-payload.webp"
              alt="ORUS-L camera payload for Holom integrations"
              fit="contain"
            />
          </div>
          <a className="holom-button holom-button--outline" href="#contact">{t("Discuss Payload Options")} <ArrowRight aria-hidden="true" /></a>
        </div>
      </section>

      <section className="holom-section holom-control">
        <div className="holom-container">
          <SectionHeading eyebrow="GROUND CONTROL & COMMUNICATIONS" title="Mission Control from the Field" copy="Holom configurations can combine portable ground-control systems, real-time telemetry, video transmission and secure command-and-control links according to mission requirements." light />
          <div className="holom-control__visuals">
            <MediaPlaceholder tone="dark" label="GCase Duo v2.1" src="/images/GCase_duo.png" alt="GCase Duo v2.1 portable ground control station" fit="contain" />
            <div className="holom-control__small-visuals"><MediaPlaceholder tone="dark" compact className="holom-controller-media" label="Portable controller" src="/images/sroc7.jpg" alt="SRoC 7 portable tactical controller" /><MediaPlaceholder tone="dark" compact className="holom-controller-media" label="Tablet controller" src="/images/sroctab5.jpg" alt="SRoC Tab 5 tablet controller" /></div>
          </div>
          <div className="holom-control__capabilities">{CONTROL_CAPABILITIES.map((item) => <IconCard key={item.title} {...item} dark />)}</div>
          <div className="holom-communications">
            <div><p className="holom-eyebrow">{t("EXTENDED MISSION COMMUNICATIONS")}</p><h3>{t("Secure links configured around the operating environment.")}</h3><p>{t("Holom can be integrated with different ground communication architectures according to mission requirements, operational environment and required data-link range.")}</p><ul><li>{t("Portable Ground Links")}</li><li>{t("Tracking Antennas")}</li><li>{t("Vehicle-Based Communications")}</li><li>{t("Encrypted Data")}</li></ul></div>
            <div className="holom-communications__visuals"><MediaPlaceholder tone="dark" compact label="Tracking antenna" src="/images/Tracking%20antenna.png" alt="GTrack tracking antenna systems" fit="contain" /><MediaPlaceholder tone="dark" compact label="Vehicle-based communications" src="/images/VCS.png" alt="VCS vehicle-based communications system" fit="contain" /></div>
          </div>
        </div>
      </section>

      <section className="holom-section holom-autonomy">
        <div className="holom-container holom-autonomy__layout">
          <div>
            <SectionHeading eyebrow="AUTONOMOUS OPERATIONS" title="Designed for Autonomous Missions" copy="Holom's flight architecture is designed to reduce operator workload and enable repeatable mission execution from takeoff through recovery." />
            <ul className="holom-check-list">{AUTONOMY_STEPS.map((step) => <li key={step}><ShieldCheck aria-hidden="true" />{t(step)}</li>)}</ul>
          </div>
        </div>
      </section>

      <section className="holom-section holom-about">
        <div className="holom-container holom-about__layout">
          <MediaPlaceholder className="holom-about-media" label="Imago engineering team" src="/images/5f7862871d80eb8dcbf6afa22ec07923a04985d1-980x626.png" alt="Imago Aerospace engineering team" />
          <div><SectionHeading eyebrow="ABOUT IMAGO AEROSPACE" title="Engineering the Next Generation of Aerial Systems" copy="Imago Aerospace develops advanced unmanned aircraft designed to make aerial operations more capable, accessible and adaptable." /><p>{t("Our team brings together aerospace engineering, composite manufacturing, avionics, autonomous systems and payload integration to develop mission-focused VTOL aircraft from airframe to flight system.")}</p><div className="holom-about__disciplines"><span><Plane aria-hidden="true" />{t("Aircraft engineering")}</span><span><Factory aria-hidden="true" />{t("Composite manufacturing")}</span><span><Radio aria-hidden="true" />{t("Systems integration")}</span></div></div>
        </div>
      </section>

      <section className="holom-section holom-contact" id="contact">
        <div className="holom-container holom-contact__layout">
          <div><SectionHeading eyebrow="START A CONVERSATION" title="Tell us about your mission." copy="Share your operating environment, mission objectives and payload needs. Our team will help evaluate the right Holom configuration." /><a className="holom-contact__email" href="mailto:hello@imago.aero"><Mail aria-hidden="true" />hello@imago.aero</a><div className="holom-contact__aside"><strong>{t("No runway.")}</strong><strong>{t("Extended coverage.")}</strong><strong>{t("Mission-ready flexibility.")}</strong></div></div>
          <ContactForm configuration={configuration} />
        </div>
      </section>

      <footer className="holom-footer">
        <div><a className="holom-logo-link" href="/" aria-label={t("Imago Aerospace home")}><img className="holom-logo" src="/CandidatoFinal%20Mini.png" alt="Imago Aerospace" /></a><p>{t("Advanced unmanned aircraft for demanding operations.")}</p></div>
        <div className="holom-footer__links"><a href="#overview">{t("Overview")}</a><a href="#specifications">{t("Specifications")}</a><a href="#contact">{t("Contact")}</a><a href="/privacy">{t("Privacy & Cookies")}</a><a href="https://www.linkedin.com/company/imago-aerospace" target="_blank" rel="noreferrer"><Linkedin aria-hidden="true" />LinkedIn</a></div>
        <p className="holom-footer__copyright">© {new Date().getFullYear()} Imago Aerospace</p>
      </footer>
    </main>
  );
}
